import React from "react";
import { Box, Divider, Flex, Text } from "@chakra-ui/react";
import { useCustomTheme } from "../../contexts/CustomThemeContext";

export type PricingBreakdown = {
    currency?: string;
    productSubtotal?: number;
    serviceFee?: number;
    deliveryFee?: number;
    discountAmount?: number;
    gatewayFee?: number;
    total?: number;

    sellerCommissionTotal?: number;
    sellerNetPayableTotal?: number;
    driverEarningTotal?: number;
    deliveryMargin?: number;
    platformNetRevenue?: number;

    deliveryDistanceKm?: number;
    totalActualWeightKg?: number;
    totalVolumetricWeightKg?: number;
    billableWeightKg?: number;
    bulkySurcharge?: number;
};

type Props = {
    breakdown?: PricingBreakdown | null;
    fallbackTotal?: number;
    title?: string;
    compact?: boolean;
    fulfilmentType?: "delivery" | "pickup";
    deliveryFeeReady?: boolean;
    pendingDeliveryMessage?: string;
};

const cardsBorderRadii ="8px";
const formatMoney = (value: number | undefined, currency = "MWK") =>
    `${currency} ${Number(value || 0).toLocaleString()}`;

const Row = ({
    label,
    value,
    strong = false,
    muted = false,
}: {
    label: string;
    value: string;
    strong?: boolean;
    muted?: boolean;
}) => {
    const { theme } = useCustomTheme();

    return (
        <Flex justify="space-between" align="center" py={1.5} gap={3}>
            <Text
                color={muted ? theme.textMuted : theme.text}
                fontWeight={strong ? "700" : "500"}
                fontSize={strong ? "md" : "sm"}
            >
                {label}
            </Text>
            <Text
                color={theme.text}
                fontWeight={strong ? "800" : "700"}
                fontSize={strong ? "md" : "sm"}
                textAlign="right"
            >
                {value}
            </Text>
        </Flex>
    );
};

const OrderPricingBreakdownCard: React.FC<Props> = ({
    breakdown,
    fallbackTotal = 0,
    title = "Price Breakdown",
    compact = false,
    fulfilmentType = "pickup",
    deliveryFeeReady = true,
    pendingDeliveryMessage = "Delivery address not set",
}) => {
    const { theme } = useCustomTheme();

    const currency = breakdown?.currency || "MWK";
    const productSubtotal = Number(breakdown?.productSubtotal || 0);
    const serviceFee = Number(breakdown?.serviceFee || 0);
    const deliveryFee = Number(breakdown?.deliveryFee || 0);
    const discountAmount = Number(breakdown?.discountAmount || 0);

    const totalPending = fulfilmentType === "delivery" && !deliveryFeeReady;

    const total =
        Number(breakdown?.total || 0) > 0
            ? Number(breakdown?.total || 0)
            : Number(fallbackTotal || 0);

    return (
        <Box
            p={4}
            borderRadius={cardsBorderRadii}
            bg={theme.surfaceAlt}
            border={`1px solid ${theme.border}`}
        >
            <Text color={theme.text} fontWeight="800" mb={3}>
                {title}
            </Text>

            {productSubtotal > 0 && (
                <Row
                    label="Products Subtotal"
                    value={formatMoney(productSubtotal, currency)}
                />
            )}

            {serviceFee > 0 && (
                <Row label="Service Fee" value={formatMoney(serviceFee, currency)} />
            )}

            {fulfilmentType === "delivery" && !deliveryFeeReady && (
                <Row
                    label="Delivery Fee"
                    value={pendingDeliveryMessage}
                    muted
                />
            )}

            {deliveryFeeReady && deliveryFee > 0 && (
                <Row label="Delivery Fee" value={formatMoney(deliveryFee, currency)} />
            )}

            {discountAmount > 0 && (
                <Row
                    label="Discount"
                    value={`- ${formatMoney(discountAmount, currency)}`}
                />
            )}

            {!compact && Number(breakdown?.bulkySurcharge || 0) > 0 && (
                <Row
                    label="Bulky Item Surcharge"
                    value={formatMoney(Number(breakdown?.bulkySurcharge || 0), currency)}
                    muted
                />
            )}

            {!compact && Number(breakdown?.deliveryDistanceKm || 0) > 0 && (
                <Row
                    label="Delivery Distance"
                    value={`${Number(breakdown?.deliveryDistanceKm || 0).toFixed(1)} km`}
                    muted
                />
            )}

            {!compact && Number(breakdown?.billableWeightKg || 0) > 0 && (
                <Row
                    label="Billable Weight"
                    value={`${Number(breakdown?.billableWeightKg || 0).toFixed(2)} kg`}
                    muted
                />
            )}

            <Divider my={3} borderColor={theme.divider} />

            {totalPending ? (
                <Row
                    label="Total"
                    value="Calculated after address selection"
                    strong
                />
            ) : (
                <Row label="Total" value={formatMoney(total, currency)} strong />
            )}
        </Box>
    );
};

export default OrderPricingBreakdownCard;